/**
 * CURATED STARTER SETS WITH MATCH SCORING
 * 
 * Each set has 3 alternatives at different match levels:
 * - Perfect Match (90-100%): Ideal for the user's exact profile
 * - Great Match (75-89%): Excellent alternative, slightly different characteristics
 * - Good Match (60-74%): Solid option, often budget-friendly
 * 
 * Updated: October 2025
 * Version: 1.0.0
 */

import { ClubType } from '../types';

export interface StarterSetClub {
  type: ClubType;
  brand: string;
  model: string;
  loft?: number;
  description: string;
  whyIncluded: string;
}

export interface StarterSetOption {
  id: string;
  name: string;
  description: string;
  matchLevel: 'perfect' | 'great' | 'good';
  matchScore: number; // 0-100
  
  // Pricing
  estimatedTotal: number;
  priceBreakdown: {
    woods: number;
    irons: number;
    wedges: number;
    putter: number;
    bag?: number;
  };
  
  // Clubs included
  clubs: StarterSetClub[];
  
  // Why this set
  keyAdvantages: string[];
  idealFor: string[];
  tradeoffs?: string[];
  
  // Recommendations
  ballRecommendation: {
    model: string;
    reason: string;
  };
  
  // Purchase info
  affiliateLinks?: {
    clubsLink?: string;
    ballsLink?: string;
  };
}

export interface StarterSetTier {
  tier: 'budget' | 'mid-range' | 'premium';
  priceRange: string;
  options: StarterSetOption[];
}

export const STARTER_SETS: StarterSetTier[] = [
  // ==================== BUDGET TIER ====================
  {
    tier: 'budget',
    priceRange: '$350 - $600',
    options: [
      {
        id: 'budget-perfect-forgiveness',
        name: 'Maximum Forgiveness Package',
        description: 'Best value clubs engineered for maximum forgiveness and easy launch',
        matchLevel: 'perfect',
        matchScore: 95,
        estimatedTotal: 549,
        priceBreakdown: {
          woods: 180,
          irons: 300,
          wedges: 40,
          putter: 29
        },
        clubs: [
          {
            type: 'driver',
            brand: 'Cobra',
            model: 'LTDx (Previous Gen)',
            loft: 10.5,
            description: '460cc head with maximum forgiveness',
            whyIncluded: 'Previous generation Cobra driver offers tour-level tech at budget price. High MOI helps with slice'
          },
          {
            type: '5-wood',
            brand: 'TaylorMade',
            model: 'Stealth (Previous Gen)',
            loft: 18,
            description: 'Versatile fairway wood',
            whyIncluded: 'Easy to hit from tee or fairway, carbon crown technology'
          },
          {
            type: '5-hybrid',
            brand: 'Wilson',
            model: 'Launch Pad',
            loft: 25,
            description: 'High-launching hybrid',
            whyIncluded: 'Specifically designed for high launch and forgiveness'
          },
          {
            type: '6-iron',
            brand: 'Wilson',
            model: 'Launch Pad Set',
            description: '6-PW+SW (6 irons)',
            whyIncluded: 'Game improvement irons with wide soles and cavity backs. Great value!'
          },
          {
            type: '7-iron',
            brand: 'Wilson',
            model: 'Launch Pad Set',
            description: 'Part of set',
            whyIncluded: 'Included in iron set'
          },
          {
            type: '8-iron',
            brand: 'Wilson',
            model: 'Launch Pad Set',
            description: 'Part of set',
            whyIncluded: 'Included in iron set'
          },
          {
            type: '9-iron',
            brand: 'Wilson',
            model: 'Launch Pad Set',
            description: 'Part of set',
            whyIncluded: 'Included in iron set'
          },
          {
            type: 'pw',
            brand: 'Wilson',
            model: 'Launch Pad Set',
            loft: 44,
            description: 'Pitching wedge',
            whyIncluded: 'Included in iron set'
          },
          {
            type: 'sw',
            brand: 'Wilson',
            model: 'Launch Pad Set',
            loft: 56,
            description: 'Sand wedge',
            whyIncluded: 'Included in iron set'
          },
          {
            type: 'putter',
            brand: 'Pinemeadow',
            model: 'PGX',
            description: 'Blade-style putter',
            whyIncluded: 'Simple, reliable putter that gets the job done'
          }
        ],
        keyAdvantages: [
          'Maximum bang for your buck',
          'All clubs engineered for forgiveness',
          'Easy to launch and hit straight',
          'Perfect for learning fundamentals',
          'Room in budget for lessons'
        ],
        idealFor: [
          'Absolute beginners',
          'Players on strict budget',
          'Those prioritizing lessons over equipment',
          'Casual players (less than monthly)',
          'Not sure if golf will stick'
        ],
        tradeoffs: [
          'Less premium feel',
          'Limited adjustability',
          'May outgrow clubs quickly if you improve fast'
        ],
        ballRecommendation: {
          model: 'Wilson Duo Soft',
          reason: 'Low compression for easier distance, great value at $20/dozen'
        }
      },
      {
        id: 'budget-great-used-premium',
        name: 'Used Premium Value Pack',
        description: '2-3 year old premium clubs in excellent condition - best performance per dollar',
        matchLevel: 'great',
        matchScore: 88,
        estimatedTotal: 595,
        priceBreakdown: {
          woods: 200,
          irons: 330,
          wedges: 35,
          putter: 30
        },
        clubs: [
          {
            type: 'driver',
            brand: 'Ping',
            model: 'G425 Max (Used)',
            loft: 10.5,
            description: 'Previous gen Ping flagship driver',
            whyIncluded: 'Ping clubs retain performance for years. Used G425 Max = premium forgiveness'
          },
          {
            type: '5-wood',
            brand: 'TaylorMade',
            model: 'SIM2 (Used)',
            loft: 18,
            description: 'Used but excellent',
            whyIncluded: 'TaylorMade fairway woods are proven performers'
          },
          {
            type: '4-hybrid',
            brand: 'Callaway',
            model: 'Mavrik (Used)',
            loft: 22,
            description: 'AI-designed hybrid',
            whyIncluded: 'Mavrik series still competes with new models'
          },
          {
            type: '5-iron',
            brand: 'TaylorMade',
            model: 'SIM2 Max OS (Used)',
            description: '5-PW+AW (7 irons)',
            whyIncluded: 'SIM2 Max OS irons = game improvement powerhouse, used condition saves $400+'
          },
          {
            type: '6-iron',
            brand: 'TaylorMade',
            model: 'SIM2 Max OS Set',
            description: 'Part of set',
            whyIncluded: 'Included'
          },
          {
            type: '7-iron',
            brand: 'TaylorMade',
            model: 'SIM2 Max OS Set',
            description: 'Part of set',
            whyIncluded: 'Included'
          },
          {
            type: '8-iron',
            brand: 'TaylorMade',
            model: 'SIM2 Max OS Set',
            description: 'Part of set',
            whyIncluded: 'Included'
          },
          {
            type: '9-iron',
            brand: 'TaylorMade',
            model: 'SIM2 Max OS Set',
            description: 'Part of set',
            whyIncluded: 'Included'
          },
          {
            type: 'pw',
            brand: 'TaylorMade',
            model: 'SIM2 Max OS Set',
            loft: 45,
            description: 'Part of set',
            whyIncluded: 'Included'
          },
          {
            type: 'gw',
            brand: 'Cleveland',
            model: 'CBX (Used)',
            loft: 50,
            description: 'Gap wedge',
            whyIncluded: 'Cleveland wedges are built to last'
          },
          {
            type: 'putter',
            brand: 'Odyssey',
            model: 'White Hot (Used)',
            description: 'Classic putter',
            whyIncluded: 'Timeless design, proven performance'
          }
        ],
        keyAdvantages: [
          'Premium brand quality at budget price',
          'Clubs that will last for years',
          'Better feel than new budget clubs',
          'Proven technology',
          'Can resell for similar price'
        ],
        idealFor: [
          'Smart shoppers',
          'Players who take care of equipment',
          'Those wanting premium brands on budget',
          'Beginners with commitment to improve'
        ],
        tradeoffs: [
          'Require shopping used market (GlobalGolf, 2ndSwing, etc.)',
          'May have minor cosmetic blemishes',
          'No manufacturer warranty'
        ],
        ballRecommendation: {
          model: 'Titleist TruFeel',
          reason: 'Quality feel at reasonable price ($25/dozen)'
        }
      },
      {
        id: 'budget-good-complete-package',
        name: 'Complete Box Set Plus',
        description: 'All-in-one complete set with bag - absolute cheapest way to start',
        matchLevel: 'good',
        matchScore: 72,
        estimatedTotal: 399,
        priceBreakdown: {
          woods: 100,
          irons: 200,
          wedges: 40,
          putter: 29,
          bag: 30
        },
        clubs: [
          {
            type: 'driver',
            brand: 'Top Flite',
            model: 'XL Complete Set',
            loft: 10.5,
            description: 'Lightweight driver',
            whyIncluded: 'Part of complete package'
          },
          {
            type: '3-wood',
            brand: 'Top Flite',
            model: 'XL Complete Set',
            loft: 15,
            description: '3-wood',
            whyIncluded: 'Part of complete package'
          },
          {
            type: '5-hybrid',
            brand: 'Top Flite',
            model: 'XL Complete Set',
            loft: 24,
            description: 'Hybrid',
            whyIncluded: 'Part of complete package'
          },
          {
            type: '6-iron',
            brand: 'Top Flite',
            model: 'XL Complete Set',
            description: '6-9, PW, SW',
            whyIncluded: 'Part of complete package'
          },
          {
            type: '7-iron',
            brand: 'Top Flite',
            model: 'XL Complete Set',
            description: 'Part of set',
            whyIncluded: 'Part of complete package'
          },
          {
            type: '8-iron',
            brand: 'Top Flite',
            model: 'XL Complete Set',
            description: 'Part of set',
            whyIncluded: 'Part of complete package'
          },
          {
            type: '9-iron',
            brand: 'Top Flite',
            model: 'XL Complete Set',
            description: 'Part of set',
            whyIncluded: 'Part of complete package'
          },
          {
            type: 'pw',
            brand: 'Top Flite',
            model: 'XL Complete Set',
            loft: 45,
            description: 'Part of set',
            whyIncluded: 'Part of complete package'
          },
          {
            type: 'sw',
            brand: 'Top Flite',
            model: 'XL Complete Set',
            loft: 56,
            description: 'Part of set',
            whyIncluded: 'Part of complete package'
          },
          {
            type: 'putter',
            brand: 'Top Flite',
            model: 'XL Complete Set',
            description: 'Mallet putter',
            whyIncluded: 'Part of complete package'
          }
        ],
        keyAdvantages: [
          'Lowest upfront cost',
          'Includes bag and headcovers',
          'Everything you need in one box',
          'Perfect for testing if you like golf',
          'Easy to buy (Amazon, Costco, etc.)'
        ],
        idealFor: [
          'Absolute beginners trying golf',
          'Extremely tight budget',
          'Gift for someone exploring golf',
          'Backup/travel set'
        ],
        tradeoffs: [
          'Basic quality',
          'Will likely want to upgrade within a year if you play regularly',
          'Less forgiveness than higher-end game improvement',
          'May not inspire confidence'
        ],
        ballRecommendation: {
          model: 'Nitro Maximum Distance',
          reason: 'Affordable bulk option, $15/2 dozen'
        }
      }
    ]
  },

  // ==================== MID-RANGE TIER ====================
  {
    tier: 'mid-range',
    priceRange: '$800 - $1,500',
    options: [
      {
        id: 'midrange-perfect-balanced',
        name: 'Balanced Performance Package',
        description: 'Modern game improvement clubs that blend forgiveness, distance, and feel',
        matchLevel: 'perfect',
        matchScore: 96,
        estimatedTotal: 1299,
        priceBreakdown: {
          woods: 450,
          irons: 650,
          wedges: 120,
          putter: 79
        },
        clubs: [
          {
            type: 'driver',
            brand: 'Cobra',
            model: 'DARKSPEED MAX',
            loft: 10.5,
            description: 'Latest game improvement driver',
            whyIncluded: 'PWR-BRIDGE tech + AI face = premium performance at mid-range price'
          },
          {
            type: '3-wood',
            brand: 'Cobra',
            model: 'DARKSPEED',
            loft: 15,
            description: 'Matching fairway wood',
            whyIncluded: 'Same tech as driver, consistent feel throughout bag'
          },
          {
            type: '4-hybrid',
            brand: 'Ping',
            model: 'G430',
            loft: 22,
            description: 'Ultra-forgiving hybrid',
            whyIncluded: 'Ping hybrids are industry-leading for ease of use'
          },
          {
            type: '5-hybrid',
            brand: 'Ping',
            model: 'G430',
            loft: 26,
            description: 'Additional hybrid for gapping',
            whyIncluded: 'Two hybrids make long game much easier'
          },
          {
            type: '6-iron',
            brand: 'Mizuno',
            model: 'JPX925 Hot Metal',
            description: '6-PW+GW (6 irons)',
            whyIncluded: 'Mizuno feel + game improvement distance = best of both worlds'
          },
          {
            type: '7-iron',
            brand: 'Mizuno',
            model: 'JPX925 Hot Metal',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '8-iron',
            brand: 'Mizuno',
            model: 'JPX925 Hot Metal',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '9-iron',
            brand: 'Mizuno',
            model: 'JPX925 Hot Metal',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: 'pw',
            brand: 'Mizuno',
            model: 'JPX925 Hot Metal',
            loft: 44,
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: 'gw',
            brand: 'Mizuno',
            model: 'JPX925 Hot Metal',
            loft: 49,
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: 'sw',
            brand: 'Cleveland',
            model: 'RTX ZipCore',
            loft: 56,
            description: 'High-spin wedge',
            whyIncluded: 'Tour-level spin at mid-range price'
          },
          {
            type: 'putter',
            brand: 'Odyssey',
            model: 'White Hot OG',
            description: 'Proven insert technology',
            whyIncluded: 'Legendary feel and consistency'
          }
        ],
        keyAdvantages: [
          'Quality brands you\'ll be proud to play',
          'Clubs that will last 5+ years',
          'Modern technology for easier golf',
          'Great feel without sacrificing forgiveness',
          'Balanced throughout the bag'
        ],
        idealFor: [
          'Committed mid-handicappers',
          'Regular players (monthly to weekly)',
          'Those who value quality and longevity',
          'Players improving steadily',
          'Anyone taking golf seriously'
        ],
        ballRecommendation: {
          model: 'Callaway Supersoft',
          reason: 'Great all-around ball with soft feel, $30/dozen'
        }
      },
      {
        id: 'midrange-great-distance-focus',
        name: 'Maximum Distance Package',
        description: 'For players who want to hit it far - longest clubs in each category',
        matchLevel: 'great',
        matchScore: 86,
        estimatedTotal: 1349,
        priceBreakdown: {
          woods: 500,
          irons: 650,
          wedges: 120,
          putter: 79
        },
        clubs: [
          {
            type: 'driver',
            brand: 'TaylorMade',
            model: 'Qi10',
            loft: 10.5,
            description: 'Maximum ball speed driver',
            whyIncluded: 'Infinity Carbon Crown = longest driver in class'
          },
          {
            type: '3-wood',
            brand: 'TaylorMade',
            model: 'Qi10',
            loft: 15,
            description: 'Hot fairway wood',
            whyIncluded: 'Matching tech, goes forever off the deck'
          },
          {
            type: '3-hybrid',
            brand: 'TaylorMade',
            model: 'Qi10',
            loft: 19,
            description: 'Long hybrid',
            whyIncluded: 'Replace 3-iron with distance and forgiveness'
          },
          {
            type: '5-hybrid',
            brand: 'TaylorMade',
            model: 'Qi10',
            loft: 25,
            description: 'Mid hybrid',
            whyIncluded: 'Consistent distance gapping'
          },
          {
            type: '6-iron',
            brand: 'TaylorMade',
            model: 'Stealth 2 HD',
            description: '6-PW+AW (6 irons)',
            whyIncluded: 'Strong lofts + Speed Pocket = maximum yardage'
          },
          {
            type: '7-iron',
            brand: 'TaylorMade',
            model: 'Stealth 2 HD',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '8-iron',
            brand: 'TaylorMade',
            model: 'Stealth 2 HD',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '9-iron',
            brand: 'TaylorMade',
            model: 'Stealth 2 HD',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: 'pw',
            brand: 'TaylorMade',
            model: 'Stealth 2 HD',
            loft: 43,
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: 'gw',
            brand: 'TaylorMade',
            model: 'Stealth 2 HD',
            loft: 48,
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: 'sw',
            brand: 'Cleveland',
            model: 'RTX ZipCore',
            loft: 56,
            description: 'Scoring wedge',
            whyIncluded: 'Control around greens'
          },
          {
            type: 'putter',
            brand: 'Odyssey',
            model: 'White Hot OG',
            description: 'Reliable putter',
            whyIncluded: 'Proven performance'
          }
        ],
        keyAdvantages: [
          'Maximum distance in every club',
          'TaylorMade brand throughout (except wedge/putter)',
          'Modern, aggressive designs',
          'High launch for carry distance',
          'Impressive on the course'
        ],
        idealFor: [
          'Players prioritizing distance',
          'Those with slower swing speeds needing help',
          'Shorter courses or tight layouts',
          'Players who struggle to reach greens'
        ],
        tradeoffs: [
          'Strong lofts may create gapping issues in scoring range',
          'Less focus on feel/control',
          'May be harder to work the ball'
        ],
        ballRecommendation: {
          model: 'Titleist Velocity',
          reason: 'Low-spin, high-speed ball for maximum distance, $35/dozen'
        }
      },
      {
        id: 'midrange-good-quality-value',
        name: 'Smart Value Package',
        description: 'Mix of current and previous gen from top brands - best value in tier',
        matchLevel: 'good',
        matchScore: 78,
        estimatedTotal: 949,
        priceBreakdown: {
          woods: 300,
          irons: 500,
          wedges: 90,
          putter: 59
        },
        clubs: [
          {
            type: 'driver',
            brand: 'Callaway',
            model: 'Rogue ST MAX (2023)',
            loft: 10.5,
            description: 'Previous gen flagship',
            whyIncluded: 'Still competitive with new models, major discount'
          },
          {
            type: '3-wood',
            brand: 'Callaway',
            model: 'Rogue ST (2023)',
            loft: 15,
            description: 'Matching fairway',
            whyIncluded: 'Great value on proven tech'
          },
          {
            type: '4-hybrid',
            brand: 'Cobra',
            model: 'LTDx (2023)',
            loft: 22,
            description: 'Forgiving hybrid',
            whyIncluded: 'Excellent hybrid at closeout price'
          },
          {
            type: '5-iron',
            brand: 'Srixon',
            model: 'ZX4 (2023)',
            description: '5-PW+GW (7 irons)',
            whyIncluded: 'Underrated brand, forged feel, great value'
          },
          {
            type: '6-iron',
            brand: 'Srixon',
            model: 'ZX4 Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '7-iron',
            brand: 'Srixon',
            model: 'ZX4 Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '8-iron',
            brand: 'Srixon',
            model: 'ZX4 Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '9-iron',
            brand: 'Srixon',
            model: 'ZX4 Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: 'pw',
            brand: 'Srixon',
            model: 'ZX4 Set',
            loft: 43,
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: 'gw',
            brand: 'Srixon',
            model: 'ZX4 Set',
            loft: 48,
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: 'sw',
            brand: 'Cleveland',
            model: 'CBX (2023)',
            loft: 56,
            description: 'Cavity-back wedge',
            whyIncluded: 'Forgiving wedge design'
          },
          {
            type: 'putter',
            brand: 'Ping',
            model: 'Fetch',
            description: 'Value putter',
            whyIncluded: 'Quality Ping putter at great price'
          }
        ],
        keyAdvantages: [
          'Best value in mid-range tier',
          'All premium brands',
          'Room in budget for lessons/accessories',
          'Previous gen = proven reliability',
          'Great performance per dollar'
        ],
        idealFor: [
          'Value-focused players',
          'Those who do not need latest models',
          'Smart shoppers',
          'Players who research before buying'
        ],
        tradeoffs: [
          'Previous generation models',
          'May not have absolute latest tech',
          'Mixed brands (not matching throughout)'
        ],
        ballRecommendation: {
          model: 'Srixon Soft Feel',
          reason: 'Excellent value ball with good performance, $22/dozen'
        }
      }
    ]
  },

  // ==================== PREMIUM TIER ====================
  {
    tier: 'premium',
    priceRange: '$1,800 - $3,000',
    options: [
      {
        id: 'premium-perfect-ultimate',
        name: 'Ultimate Performance Package',
        description: 'Best of the best - tour-level performance across the board',
        matchLevel: 'perfect',
        matchScore: 98,
        estimatedTotal: 2899,
        priceBreakdown: {
          woods: 950,
          irons: 1400,
          wedges: 350,
          putter: 199
        },
        clubs: [
          {
            type: 'driver',
            brand: 'Titleist',
            model: 'TSR3',
            loft: 9,
            description: 'Tour-proven driver',
            whyIncluded: 'Best of the best, endless adjustability, tour pedigree'
          },
          {
            type: '3-wood',
            brand: 'Titleist',
            model: 'TSR2',
            loft: 15,
            description: 'Versatile 3-wood',
            whyIncluded: 'Perfect complement to TSR3 driver'
          },
          {
            type: '5-wood',
            brand: 'Titleist',
            model: 'TSR2',
            loft: 18,
            description: '5-wood option',
            whyIncluded: 'Additional fairway option for versatility'
          },
          {
            type: '4-hybrid',
            brand: 'Titleist',
            model: 'TSR2',
            loft: 21,
            description: 'Titleist hybrid',
            whyIncluded: 'Matching Titleist for consistent feel'
          },
          {
            type: '5-iron',
            brand: 'Titleist',
            model: 'T200',
            description: '5-PW (6 irons)',
            whyIncluded: 'Players distance iron with amazing feel and forgiveness'
          },
          {
            type: '6-iron',
            brand: 'Titleist',
            model: 'T200 Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '7-iron',
            brand: 'Titleist',
            model: 'T200 Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '8-iron',
            brand: 'Titleist',
            model: 'T200 Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '9-iron',
            brand: 'Titleist',
            model: 'T200 Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: 'pw',
            brand: 'Titleist',
            model: 'T200 Set',
            loft: 45,
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: 'gw',
            brand: 'Titleist',
            model: 'Vokey SM10',
            loft: 50,
            description: 'Gap wedge',
            whyIncluded: 'Tour wedges for ultimate spin and control'
          },
          {
            type: 'sw',
            brand: 'Titleist',
            model: 'Vokey SM10',
            loft: 54,
            description: 'Sand wedge F grind',
            whyIncluded: 'Versatile grind for all conditions'
          },
          {
            type: 'lw',
            brand: 'Titleist',
            model: 'Vokey SM10',
            loft: 60,
            description: 'Lob wedge M grind',
            whyIncluded: 'High bounce for soft landings'
          },
          {
            type: 'putter',
            brand: 'Odyssey',
            model: 'Ai-ONE Cruiser',
            description: 'Premium Odyssey',
            whyIncluded: 'AI-designed insert for consistent roll'
          }
        ],
        keyAdvantages: [
          'Titleist throughout long game',
          'Tour-proven performance',
          'Premium materials and craftsmanship',
          'Will last 10+ years',
          'Best possible performance',
          'Pride of ownership'
        ],
        idealFor: [
          'Serious golfers',
          'Low handicappers',
          'Those who play frequently',
          'Players who want the absolute best',
          'Equipment enthusiasts'
        ],
        ballRecommendation: {
          model: 'Titleist Pro V1',
          reason: 'Best ball in golf, matches premium equipment, $55/dozen'
        }
      },
      {
        id: 'premium-great-mixed-best',
        name: 'Best-in-Class Mix Package',
        description: 'Cherry-pick the best club from each brand - ultimate custom bag',
        matchLevel: 'great',
        matchScore: 92,
        estimatedTotal: 2649,
        priceBreakdown: {
          woods: 850,
          irons: 1200,
          wedges: 350,
          putter: 249
        },
        clubs: [
          {
            type: 'driver',
            brand: 'TaylorMade',
            model: 'Qi10',
            loft: 9,
            description: 'Longest driver',
            whyIncluded: 'TaylorMade leads in driver distance technology'
          },
          {
            type: '3-wood',
            brand: 'Ping',
            model: 'G430 Max',
            loft: 15,
            description: 'Most forgiving fairway',
            whyIncluded: 'Ping fairway woods = easiest to hit'
          },
          {
            type: '5-wood',
            brand: 'Ping',
            model: 'G430',
            loft: 18,
            description: 'Matching fairway',
            whyIncluded: 'Consistency with 3-wood'
          },
          {
            type: '4-hybrid',
            brand: 'Ping',
            model: 'G430',
            loft: 22,
            description: 'Best hybrid',
            whyIncluded: 'Nobody makes hybrids like Ping'
          },
          {
            type: '5-iron',
            brand: 'Callaway',
            model: 'Paradym',
            description: '5-PW (6 irons)',
            whyIncluded: 'Callaway AI-designed irons = best blend of distance, feel, forgiveness'
          },
          {
            type: '6-iron',
            brand: 'Callaway',
            model: 'Paradym Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '7-iron',
            brand: 'Callaway',
            model: 'Paradym Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '8-iron',
            brand: 'Callaway',
            model: 'Paradym Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '9-iron',
            brand: 'Callaway',
            model: 'Paradym Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: 'pw',
            brand: 'Callaway',
            model: 'Paradym Set',
            loft: 43,
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: 'gw',
            brand: 'Titleist',
            model: 'Vokey SM10',
            loft: 50,
            description: 'Gap wedge',
            whyIncluded: 'Vokey = tour standard for wedges'
          },
          {
            type: 'sw',
            brand: 'Titleist',
            model: 'Vokey SM10',
            loft: 54,
            description: 'Sand wedge',
            whyIncluded: 'M grind versatility'
          },
          {
            type: 'lw',
            brand: 'Titleist',
            model: 'Vokey SM10',
            loft: 60,
            description: 'Lob wedge',
            whyIncluded: 'L grind for flop shots'
          },
          {
            type: 'putter',
            brand: 'Ping',
            model: 'Heppler Anser',
            description: 'Classic design',
            whyIncluded: 'Proven Anser shape with modern tech'
          }
        ],
        keyAdvantages: [
          'Best club from each category',
          'Mixed brands based on performance',
          'Maximum performance per club',
          'Unique, personalized setup',
          'Tour-level across the board'
        ],
        idealFor: [
          'Players who research deeply',
          'Those wanting best-in-class everywhere',
          'Mid to low handicappers',
          'Equipment optimization enthusiasts'
        ],
        tradeoffs: [
          'No brand matching',
          'Varied feel/aesthetics',
          'Requires understanding each brand'
        ],
        ballRecommendation: {
          model: 'Callaway Chrome Soft',
          reason: 'Premium urethane ball with excellent feel, $50/dozen'
        }
      },
      {
        id: 'premium-good-progressive',
        name: 'Progressive Performance Package',
        description: 'Forgiving woods, players irons - designed for improving player',
        matchLevel: 'good',
        matchScore: 82,
        estimatedTotal: 2299,
        priceBreakdown: {
          woods: 750,
          irons: 1200,
          wedges: 250,
          putter: 99
        },
        clubs: [
          {
            type: 'driver',
            brand: 'Ping',
            model: 'G430 Max',
            loft: 10.5,
            description: 'Forgiving driver',
            whyIncluded: 'Keep long game forgiving while you develop'
          },
          {
            type: '3-wood',
            brand: 'Ping',
            model: 'G430',
            loft: 16.5,
            description: 'High launch fairway',
            whyIncluded: 'Easy consistency off the deck'
          },
          {
            type: '4-hybrid',
            brand: 'Ping',
            model: 'G430',
            loft: 22,
            description: 'Utility hybrid',
            whyIncluded: 'Forgiveness in long game'
          },
          {
            type: '5-iron',
            brand: 'Srixon',
            model: 'ZX5 Mk II',
            description: '5-PW (6 irons)',
            whyIncluded: 'Players cavity back for feel with some forgiveness'
          },
          {
            type: '6-iron',
            brand: 'Srixon',
            model: 'ZX5 Mk II Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '7-iron',
            brand: 'Srixon',
            model: 'ZX5 Mk II Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '8-iron',
            brand: 'Srixon',
            model: 'ZX5 Mk II Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: '9-iron',
            brand: 'Srixon',
            model: 'ZX5 Mk II Set',
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: 'pw',
            brand: 'Srixon',
            model: 'ZX5 Mk II Set',
            loft: 45,
            description: 'Part of set',
            whyIncluded: 'Part of iron set'
          },
          {
            type: 'gw',
            brand: 'Cleveland',
            model: 'RTX ZipCore',
            loft: 50,
            description: 'Gap wedge',
            whyIncluded: 'High spin wedges'
          },
          {
            type: 'sw',
            brand: 'Cleveland',
            model: 'RTX ZipCore',
            loft: 56,
            description: 'Sand wedge',
            whyIncluded: 'Versatile mid grind'
          },
          {
            type: 'putter',
            brand: 'Odyssey',
            model: 'White Hot OG',
            description: 'Proven putter',
            whyIncluded: 'Best-selling putter line ever'
          }
        ],
        keyAdvantages: [
          'Progressive difficulty',
          'Forgiving where you need it',
          'Room to grow with irons',
          'Excellent value in premium tier',
          'Balanced approach'
        ],
        idealFor: [
          'Rapidly improving players',
          'Mid handicappers moving to low',
          'Those wanting to grow into clubs',
          'Players with inconsistent ball striking'
        ],
        tradeoffs: [
          'Not matching brands',
          'Irons may be challenging initially',
          'Woods/irons feel different'
        ],
        ballRecommendation: {
          model: 'TaylorMade TP5',
          reason: 'Tour-level ball with versatile performance, $50/dozen'
        }
      }
    ]
  }
];

// Helper function to get options by tier
export function getStarterSetsByTier(tier: 'budget' | 'mid-range' | 'premium'): StarterSetOption[] {
  const tierData = STARTER_SETS.find(t => t.tier === tier);
  return tierData?.options || [];
}

// Helper to calculate match score based on user profile
export function calculateMatchScore(
  option: StarterSetOption,
  userProfile: {
    handicap: number;
    budget: number;
    skillLevel: string;
    courseFrequency: string;
  }
): number {
  let score = option.matchScore;
  
  // Adjust based on budget fit
  const budgetDiff = Math.abs(option.estimatedTotal - userProfile.budget);
  if (budgetDiff > 500) score -= 10;
  else if (budgetDiff > 200) score -= 5;
  
  // Adjust based on play frequency
  if (userProfile.courseFrequency === 'rarely' && option.estimatedTotal > 800) score -= 5;
  if (userProfile.courseFrequency === 'multiple' && option.estimatedTotal < 1000) score -= 5;
  
  return Math.max(60, Math.min(100, score));
}

export const STARTER_SETS_VERSION = {
  version: '1.0.0',
  lastUpdated: '2025-10-01',
  setCount: STARTER_SETS.reduce((acc, tier) => acc + tier.options.length, 0),
  nextUpdateDue: '2026-01-01'
};

